import { fieldConfig } from "@autoform/zod";
import { z } from "zod";

import { MediaLenient } from "@/components/forms/schemas/media";

const disabled = fieldConfig({ inputProps: { disabled: true } });

/** UpdateTeamCommand, curator-owned members only (TEAM_EDITABLE). */
export const teamEditSchema = z.object({
  id: z.string().nullish().superRefine(disabled),
  shortName: z.string().nullish(),
  city: z.string().nullish(),
  homeVenueId: z
    .string()
    .nullish()
    .superRefine(
      fieldConfig({
        fieldType: "teamHomeVenue",
        label: "Home stadium",
        description:
          "Future matches move to this stadium on TINA's next daily run. Matches with a stadium set by hand keep theirs.",
      }),
    ),
  foundedYear: z.coerce
    .number()
    .int()
    .refine(
      (y) => y === 0 || (y >= 1800 && y <= new Date().getFullYear()),
      "A year from 1800 to this year, or 0 to clear",
    )
    .nullish()
    .superRefine(
      fieldConfig({
        label: "Founded",
        description:
          "From Wikidata, filled by TINA only when empty. Clear it to remove it.",
      }),
    ),
  nicknames: z
    .object({
      el: z.string().max(100).nullish(),
      en: z.string().max(100).nullish(),
    })
    .nullish()
    .superRefine(
      fieldConfig({
        label: "Nicknames",
        description:
          "Each in its own language, never translated. Clear one to remove it.",
      }),
    ),
  wikidataId: z
    .string()
    .regex(/^(Q[1-9][0-9]*)?$/, "A Wikidata item id such as Q19628")
    .nullish()
    .superRefine(
      fieldConfig({
        label: "Wikidata ID",
        description:
          "Set it when TINA found no match; the next match of this team fills the empty facts from it.",
      }),
    ),
  status: z.enum(["Draft", "Complete", "Archived"]),
  hide: z.boolean().default(false),
  media: MediaLenient.nullish().superRefine(
    fieldConfig({ fieldType: "media", customData: { collection: "Teams" } }),
  ),
});
export type TeamEditType = z.infer<typeof teamEditSchema>;

/**
 * The backend reads an omitted member as "keep", so a cleared field has to be sent as its clear
 * signal: `foundedYear: 0`, `wikidataId: ""`, and `""` for a cleared nickname locale (the PUT merges
 * `nicknames` per locale, so an omitted locale would be kept).
 */
export function teamFactsForSubmit(
  dirty: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...dirty };
  if ("foundedYear" in out) {
    const year = out.foundedYear;
    out.foundedYear =
      typeof year === "number" && Number.isFinite(year) ? year : 0;
  }
  if ("wikidataId" in out) {
    const id = typeof out.wikidataId === "string" ? out.wikidataId.trim() : "";
    out.wikidataId = id;
  }
  if ("nicknames" in out) {
    const raw = (out.nicknames ?? {}) as Record<string, unknown>;
    out.nicknames = Object.fromEntries(
      ["el", "en"].map((locale) => {
        const value = raw[locale];
        return [locale, typeof value === "string" ? value.trim() : ""];
      }),
    );
  }
  return out;
}
